<template>
    <nav>
        <AlertMessages
            v-if="alertValue"
            :alert-type="alertType" 
            :alert-msg="alertMsg"
            @reset="alertValue = false"
        />
        <v-row
            v-if="!loadingData && storeProduct.attributes && storeProduct.relationships"
            align="center"
            class="mx-auto"
        >
            <v-col
                cols="11"
                class="mx-auto px-0"
            >
                <p class="text-h6">{{ storeProduct.attributes.title }} <v-chip v-if="storeProduct.attributes.is_archived == true" color="error" data-testid="chip-order-denied" class="rounded-xxl text-none px-4 white--text mr-2">{{ $t('archived') }}</v-chip></p>
                <v-row>
                    <v-col cols="12" md="6" lg="6">
                        <p class="text-body-1">{{$t('connectedProductTitle')}}</p>
                        <template v-if="storeProduct.attributes.session_type == 'single'">
                          <v-hover>
                              <v-card slot-scope="{ hover }" rounded="xxl" :class="`elevation-${hover? 8 : 2 }`" data-testid="product-entity-card">
                                  <span v-if="storeProduct.relationships.productable.data.type === 'activities'" class="pa-5 d-block">{{ $t('connectedProductActivity') }}: {{ storeProduct.attributes.productable.title }}</span>
                                  <span v-else-if="storeProduct.relationships.productable.data.type === 'hubs'" class="pa-5 d-block">{{ $t('connectedProductHub') }}: {{ storeProduct.attributes.productable.title }}</span>
                                  <span v-else class="pa-5 d-block">{{ $t('connectedProductWorkflow') }}: {{ storeProduct.attributes.productable.title }}</span>
                                  <span v-if="storeProduct.attributes.end_date" class="pa-5 d-block">{{ $t('sessionEndDate') + ': ' + format_date(storeProduct.attributes.end_date) }}</span>
                              </v-card>
                          </v-hover>
                        </template>
                        <template v-else data-testid="product-entity-multiple">
                          <v-hover v-for="(session, index) in productSessions" :key="index+session.id" >
                            <v-card slot-scope="{ hover }" rounded="xxl" :class="`elevation-${hover? 8 : 2 } d-flex mb-3 justify-space-between`" :data-testid="`product-entity-card-${index}`">
                              <v-col cols="4" class="pa-5 d-block">{{ session.attributes.session_title }} </v-col>
                             
                              <v-col cols="4" v-if="session.relationships.productable.data.type === 'activities'" class="pa-5 d-block">{{ $t('connectedProductActivity') }}: {{ session.attributes.productable.title }}</v-col>
                              <v-col cols="4" v-else-if="session.relationships.productable.data.type === 'hubs'" class="pa-5 d-block">{{ $t('connectedProductHub') }}: {{ session.attributes.productable.title }}</v-col>
                              <v-col cols="4" v-else class="pa-5 d-block">{{ $t('connectedProductWorkflow') }}: {{ session.attributes.productable.title }}</v-col>

                              <v-col cols="4" class="pa-5 d-block">{{ session.attributes.end_date ? $t('sessionEndDate') + ': ' + format_date(session.attributes.end_date) : '' }}</v-col>
                            </v-card>
                          </v-hover>
                        </template>
                    </v-col>
                    <v-col cols="12" md="6" lg="6" >
                        <p>{{ $t('connectedProductPage') }}</p>
                        <v-hover>
                            <v-card slot-scope="{ hover }" rounded="xxl" :class="`elevation-${hover? 8 : 2 }`">
                                <v-row no-gutters class="pa-5" v-if="!storeProduct.attributes.is_archived">
                                    <v-col cols="10">
                                        <a class="text-caption" :href="storeProductURL" target="_blank">{{ storeProductURL }}</a>
                                    </v-col>
                                    <v-col cols="2" class="text-right">
                                        <v-icon small class="ml-1" @click="copyToClipboard(storeProductURL)">mdi-content-copy</v-icon>
                                    </v-col>
                                </v-row>
                                <v-row no-gutters class="pa-5" v-else>
                                    <v-col cols="12">
                                        {{ $t("notAvailableForOrders")}}
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-hover>
                    </v-col>  
                </v-row>
            </v-col>
        </v-row>
        <v-row 
          v-if="!loadingData && storeProduct.attributes && storeProduct.relationships"
            align="center"
            class="mx-auto"
          >
            <v-col
                cols="11"
                class="mx-auto px-0"
            >
                <p class="text-h6">{{ $t('chartsTitle') }}</p>
                <v-row>
                  <v-col cols="12" lg="4" md="6">
                    <v-card rounded="xxl" class="pa-5">
                      <KpiChart 
                        :title="$t('chartsTotalParticipantsTitle')"
                        :total="totalParticipants" 
                        :data="participantsByMonth"
                        :text-left="$t('participants')"
                        :downloaded-file-title="'Total-number-participants-' + storeProduct.attributes.title"
                        @getNewChartData="getNewChartData"
                        id="KpiParticipants"
                        data-testid="kpi-participants"
                      />
                    </v-card>
                  </v-col>
                  <v-col cols="12" lg="4" md="6">
                    <v-card rounded="xxl" class="pa-5">
                      <KpiChart
                        :title="$t('chartsTotalOrdersTitle')"
                        :total="totalOrders" 
                        :data="ordersByMonth"
                        :text-left="$t('orders')"
                        :downloaded-file-title="'Total-number-orders-' + storeProduct.attributes.title"
                        @getNewChartData="getNewChartData"
                        id="KpiOrders"
                        data-testid="kpi-orders"
                      />
                    </v-card>
                </v-col>
                <v-col cols="12" lg="4" md="6">
                    <v-card rounded="xxl" class="pa-5">
                      <KpiChart
                        :title="$t('chartsTotalSalesTitle')" 
                        :total="totalSales" 
                        :data="salesByMonth"
                        :text-left="$t('sales')"
                        :downloaded-file-title="'Total-sales-' + storeProduct.attributes.title"
                        @getNewChartData="getNewChartData"
                        id="KpiSales"
                        data-testid="kpi-sales"
                      />
                    </v-card>
                </v-col>
              </v-row>
            </v-col>
        </v-row>
        <v-row v-if="storeProduct.attributes" align="center" class="mx-auto">
            <v-col :cols="storeProduct.attributes.session_type !== 'single' ? '8' : '11'" class="mx-auto px-0 pb-0 mt-6">
                <p class="text-h6">{{$t('ordersTitle') }}</p>
            </v-col> 
            <v-col v-if="storeProduct.attributes.session_type !== 'single'" cols="2" class="mx-auto px-0 pb-0 mt-6">
              <v-menu
                  v-model="filterSession"
                  width="360"
                  max-width="360"
                  style="border-radius: 16px;"
              >
                  <template v-slot:activator="{ on, attrs }">
                      <v-btn
                          v-bind="attrs"
                          v-on="on"
                          class="rounded-xxl text-none px-4 filter-btn"
                          data-testid="orders-filter-button"
                      >
                          {{ selectedSession ? selectedSession : $t('allSessions') }}
                          <v-icon
                              right
                              color="accent"
                          >
                              mdi-chevron-down
                          </v-icon>
                      </v-btn>
                  </template>
                  <v-list class="session-filter rounded-xxl">
                    <v-list-item data-testid="orders-filter-all" class="session-filter-item" @click="filterOrders(null)">
                        <v-list-item-title>{{ $t('allSessions') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item class="session-filter-item" v-for="session in productSessions" :key="session.id" data-testid="orders-filter-pending" @click="filterOrders(session)">
                        <v-list-item-title>{{ session.attributes.session_title }}</v-list-item-title>
                      </v-list-item>
                  </v-list>
              </v-menu>
            </v-col>
        </v-row>
        <v-row v-if="filteredOrders.length == 0 && !loadingData">
          <v-col
              cols="11"
              class="mx-auto"
            >
               <p class="grey--text ">{{ $t('orderNoResultProductPage') }}</p>
          </v-col>
        </v-row>
        <v-row
            data-testid="product-orders-list"
            v-if="filteredOrders.length > 0 && !loadingData"
            align="center"
            class="mx-auto"
          >
            <v-col
              cols="11"
              class="mx-auto px-0"
            >
              <v-card
                v-for="(order) in filteredOrders"
                :key="order.id"
                :ripple="false"
                data-testid="store-order-card"
                rounded="xxl"
                class="my-2 solo"
                @click="showOrderDetail(order.uuid)"
              >
                <v-hover v-if="mobile()">
                  <v-row
                    v-if="mobile()"
                    slot-scope="{ hover }"
                    no-gutters
                    :class="`py-3 elevation-${hover? 8 : 2 }`"
                  >
                    <v-col
                      cols="11"
                      align-self="center"
                      class="px-6"
                    >
                      <v-card-text class="text-body-2 darktext--text pa-0 text-no-wrap text-truncate d-block">
                        {{ order.company_name }}
                      </v-card-text>
                    </v-col>
              
                  </v-row>
                </v-hover>
                <v-hover v-if="!mobile()">
                  <v-row
                    v-if="!mobile()"
                    slot-scope="{ hover }"
                    no-gutters
                    :class="`py-3 elevation-${hover? 8 : 2 }`"
                  >
                    <v-col
                      cols="11"
                      md="4"
                      class="px-6"
                    >
                      <div class="text-body-2 grey--text justify-left">
                        {{ $t('labelCompany') }}
                      </div>
                      <div class="text-body-1 darktext--text ">
                        {{ order.company_name }}
                      </div>
                    </v-col>
                     <v-col
                      cols="11"
                      md="3"
                      class="px-6"
                    >
                      <div class="text-body-2 grey--text justify-left">
                        {{ $t('labelOrderNumber') }}
                      </div>
                      <div class="text-body-1 darktext--text ">
                        {{ order.number}}
                      </div>
                    </v-col>
                    <v-col
                      cols="11"
                      md="4"
                      class="px-6"
                      align="right"
                    >
                      <div class="text-body-1 grey--text mt-3">
                        {{ $t('labelDateCreated') }} : {{ format_date(order.created_at) }}
                      </div>
                    </v-col>
                    
                  </v-row>
                </v-hover>
              </v-card>
          </v-col>
        </v-row>
    </nav>
</template>
<script>
import i18n from '../../plugins/i18n'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin'
import { getStoreProduct, getStoreProductOverview, getProductSessions} from '../../api/productApi'
import AlertMessages  from '../../components/AlertMessages'
import moment from 'moment'
import KpiChart from '../../components/KpiChart.vue'

export default {
    components: {
        AlertMessages,
        KpiChart
    },
    mixins: [repeatedMethodsMixin],
    data(){
        return{
            alertType: '',
            alertMsg: '',
            alertValue:false,
            spaceId: window.$cookies.get('space_id'),
            loadingData: false,
            storeProduct: {},
            storeProductTitle: '',
            storeProductDescription: '',
            storeProductType: '',
            storeProductURL: '',
            productID: this.$route.params.id,
            space: window.location.host.split('.')[0],
            productOrders: [],
            totalParticipants: 0,
            totalOrders: 0,
            totalSales: 0,
            participantsByMonth: [],
            ordersByMonth: [],
            salesByMonth: [],
            filterSession: false,
            selectedSession: null,
            filteredOrders: [],
            productSessions: [],
        }
    },
    async created(){      
        this.loadingData = true
        await this.fetchStoreProduct()
        this.loadingData = false

        if(this.$route.params.successfullyDuplicatedProduct){
            this.alertMsg = i18n.t('duplicateProductSuccess')
            this.alertType = 'success'
            this.alertValue = true
        }
    },

    methods:{
        filterOrders(session){
          this.selectedSession = session ? session.attributes.session_title : null;
          if (session) {
            this.filteredOrders = this.productOrders.filter(
              (order) => order.session_id == session.id
            );
          } else {
            this.filteredOrders = this.productOrders;
          }
        },
        async fetchStoreProduct() {
            try {
                const response = await getStoreProduct(this.productID);
                if (response.status == 200) {
                    this.storeProduct = response.data.data
                    this.storeProductURL = process.env.VUE_APP_PROTOCOL + "://" + this.space + "." + process.env.VUE_APP_DOMAIN + "/product/" + this.productID
                    this.productOrders = response.data.data.attributes.orders;
                    this.filteredOrders = this.productOrders;
                    if(this.storeProduct.attributes.session_type == "multiple") {
                      await this.fetchSessions(this.storeProduct.id);
                    }
                }
            } catch (e) {
                if(e.response){
                    if(e.response.status == 404) {
                        this.$router.push({name: 'PageNotFound'})
                    }
                    else if(e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                }
            }
        },

        async fetchSessions(id){
          try {
              const response = await getProductSessions(id);
              if(response.status == 200) {
                  this.productSessions = response.data.data;
              }  
          } catch (e) {
              if (e.response) {
                  this.alertValue = true;
                  this.alertType = "error";
                  this.alertMsg = i18n.t("serverError");
              }
          }
        },
        copyToClipboard(link){
            const el = document.createElement('textarea');  
            el.value = link
            document.body.appendChild(el);                          
            el.select();                                    
            document.execCommand('copy');                   
            document.body.removeChild(el);                  
            this.alertMsg = i18n.t('copyProductLinkSuccessMsg');
            this.alertType = 'success' 
            this.alertValue = true  
        },
        showOrderDetail(id){
            this.$router.push({path: '/store/order/' + id })
        },
        format_date(value){
            if (value) {
                return moment(String(value)).format('DD MMM YYYY')
            }
        },
        async getNewChartData(id, created, startDate, endDate, currency){
            if(created == true){
                if(id === "KpiParticipants"){
                    try {
                        const response = await getStoreProductOverview(this.productID, startDate, endDate, currency);
                        if (response.status == 200) {
                            const data = response.data.meta
                            this.totalParticipants = data.total_participants
                            this.participantsByMonth = data.participants_by_month
                            this.totalOrders = data.total_orders
                            this.ordersByMonth = data.orders_by_month
                            this.totalSales = parseInt(data.total_sales)
                            this.salesByMonth = data.sales_by_month
                        }
                    } catch (e) {
                        if(e.response){
                            if(e.response.status == 404) {
                                this.$router.push({name: 'PageNotFound'})
                            }
                            else if(e.response.status == 403){
                                this.$router.push({name: 'PageAccessDenied'})
                            } else if(e.response.status == 400){
                              for(let i = 0; i < e.response.data.errors.length; i++){
                                  if(e.response.data.errors[i].detail == 'Invalid date parameters!'){
                                    this.alertValue = true
                                    this.alertType = 'error'  
                                    this.alertMsg = i18n.t('invalidDateError');
                                  }else {
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg = i18n.t('serverError')
                                  }
                              }
                            }else {
                                this.alertValue = true
                                this.alertType = 'error'
                                this.alertMsg =  i18n.t('serverError')
                            }
                        }
                    }
                }
            }else{
                try {
                    const response = await getStoreProductOverview(this.productID, startDate, endDate, currency);
                    if (response.status == 200) {
                        const data = response.data.meta
                        if (id === "KpiParticipants") {
                            this.totalParticipants = data.total_participants
                            this.participantsByMonth = data.participants_by_month
                        } else if (id === "KpiOrders") {
                            this.totalOrders = data.total_orders
                            this.ordersByMonth = data.orders_by_month
                        } else if (id === "KpiSales") {
                            this.totalSales = parseInt(data.total_sales)
                            this.salesByMonth = data.sales_by_month
                        }
                    }
                } catch (e) {
                    if(e.response){
                        if(e.response.status == 404) {
                            this.$router.push({name: 'PageNotFound'})
                        }
                        else if(e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else if(e.response.status == 400){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'Invalid date parameters!'){
                                  this.alertValue = true
                                  this.alertType = 'error'  
                                  this.alertMsg = i18n.t('invalidDateError');
                                }else {
                                  this.alertValue = true
                                  this.alertType = 'error'
                                  this.alertMsg = i18n.t('serverError')
                                }
                            }
                        }else {
                            this.alertValue = true
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }
                }
            }
        }
    }
}
</script>  
<style>
.filter-btn {
    max-width: 100%;
    width: 100%;
}
.filter-btn span {
    text-overflow: ellipsis;
    width: 100%;
    display: block;
    overflow: hidden;
}
.session-filter-item {
    min-height: 32px;
}
</style>